import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ChevronDown, CreditCard, Plus, Save, Trash2 } from 'lucide-react';
import { useAuth } from '@/shared/lib/auth';
import { usePassbook } from '@/shared/lib/passbook';
import { canAccessSuperAdmin } from '@/shared/lib/navigation';
import { cn } from '@/shared/lib/cn';
import { PageHeader } from '@/shared/components/page-header';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Switch } from '@/shared/ui/switch';
import { HtmlEditor } from '@/shared/ui/html-editor';
import { updateBackFields } from '../api/use-verso-cartao';
import { mgmFabUrl } from '../model/mgm-back-link';
import {
  BACK_FIELD_BLOCKS,
  MAX_BACK_FIELD_BLOCKS,
  buildSavePayload,
  countVisibleBlocks,
  passbookToBackFields,
  type BackFieldsPayload,
} from '../model/back-fields';
import { htmlToWalletValue, walletValueToEditorHtml } from '../model/wallet-markup';
import { VersoWalletPreview } from './verso-wallet-preview';

/** Carrega os blocos já no formato Wallet (quebra de linha + `<a>`), inclusive HTML antigo do TipTap. */
function toWalletFields(passbook: Parameters<typeof passbookToBackFields>[0]): BackFieldsPayload {
  const next = passbookToBackFields(passbook);
  for (const block of BACK_FIELD_BLOCKS) {
    next[block.valueKey] = htmlToWalletValue(next[block.valueKey] ?? '');
  }
  return next;
}

/** Edita os blocos do verso (accordion) com preview Wallet ao lado. Só super-admin. */
export function VersoCartaoPage() {
  const { user } = useAuth();
  const { idPassbook, passbook } = usePassbook();
  const queryClient = useQueryClient();
  const [fields, setFields] = useState<BackFieldsPayload>(() => toWalletFields(passbook));
  const [visibleCount, setVisibleCount] = useState(() =>
    countVisibleBlocks(toWalletFields(passbook)),
  );
  const [openIds, setOpenIds] = useState<Set<string>>(() => new Set(['1']));
  const [showMgmBackLink, setShowMgmBackLink] = useState(() => passbook.showMgmBackLink === true);
  const [saving, setSaving] = useState(false);
  const [savingMgm, setSavingMgm] = useState(false);
  const mgmUrl = mgmFabUrl(passbook.fabs);
  const fieldsKey = `${idPassbook}:${BACK_FIELD_BLOCKS.map((block) => `${passbook[block.labelKey] ?? ''}\0${passbook[block.valueKey] ?? ''}`).join('\n')}`;

  useEffect(() => {
    const next = toWalletFields(passbook);
    setFields(next);
    const count = countVisibleBlocks(next);
    setVisibleCount(count);
    setOpenIds(new Set([BACK_FIELD_BLOCKS[0].id]));
    // fieldsKey resume os blocos salvos. O passbook também muda quando só o interruptor grava.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldsKey]);

  useEffect(() => {
    setShowMgmBackLink(passbook.showMgmBackLink === true);
  }, [passbook.showMgmBackLink]);

  if (!user || !canAccessSuperAdmin(user.email)) {
    return <Navigate to={`/loja/${idPassbook}`} replace />;
  }

  const visibleBlocks = BACK_FIELD_BLOCKS.slice(0, visibleCount);

  const patch = (key: string, value: string) => {
    setFields((prev) => ({ ...prev, [key]: value }));
  };

  const toggleOpen = (id: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const addBlock = () => {
    setVisibleCount((n) => {
      const next = Math.min(n + 1, MAX_BACK_FIELD_BLOCKS);
      const newId = BACK_FIELD_BLOCKS[next - 1]?.id;
      if (newId) setOpenIds((prev) => new Set(prev).add(newId));
      return next;
    });
  };

  const removeLastBlock = () => {
    if (visibleCount <= 1) return;
    const last = BACK_FIELD_BLOCKS[visibleCount - 1];
    setFields((prev) => ({
      ...prev,
      [last.labelKey]: '',
      [last.valueKey]: '',
    }));
    setOpenIds((prev) => {
      const next = new Set(prev);
      next.delete(last.id);
      return next;
    });
    setVisibleCount((n) => n - 1);
  };

  const handleMgmToggle = async (next: boolean) => {
    const previous = showMgmBackLink;
    setShowMgmBackLink(next);
    setSavingMgm(true);
    try {
      await updateBackFields(idPassbook, { showMgmBackLink: next });
      queryClient.setQueryData<{ passbook: typeof passbook } | undefined>(
        ['passbook', idPassbook],
        (current) =>
          current
            ? { ...current, passbook: { ...current.passbook, showMgmBackLink: next } }
            : current,
      );
    } catch (err) {
      setShowMgmBackLink(previous);
      toast.error(
        err instanceof Error ? err.message : 'Não foi possível atualizar o Indique e ganhe.',
      );
    } finally {
      setSavingMgm(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateBackFields(
        idPassbook,
        buildSavePayload(fields, visibleCount, mgmUrl ? showMgmBackLink : undefined),
      );
      await queryClient.invalidateQueries({ queryKey: ['passbook', idPassbook] });
      toast.success('Verso do cartão salvo.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Não foi possível salvar o verso.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Verso do cartão"
        description="Enter quebra a linha como no Wallet."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={addBlock}
              disabled={visibleCount >= MAX_BACK_FIELD_BLOCKS}
            >
              <Plus className="size-4" />
              Adicionar bloco
              <span className="text-muted-foreground">
                ({visibleCount}/{MAX_BACK_FIELD_BLOCKS})
              </span>
            </Button>
            <Button onClick={() => void handleSave()} loading={saving}>
              <Save className="size-4" />
              Salvar
            </Button>
          </div>
        }
      />

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(280px,360px)]">
        <div className="flex min-w-0 flex-col gap-2">
          {mgmUrl ? (
            <div className="flex items-center justify-between gap-3 rounded-xl border border-border/70 bg-card px-3 py-3 shadow-glass-sm">
              <p className="text-sm font-semibold">Indique e ganhe</p>
              <Switch
                checked={showMgmBackLink}
                disabled={savingMgm}
                onCheckedChange={(next) => void handleMgmToggle(next)}
                aria-label="Mostrar Indique e ganhe no verso"
              />
            </div>
          ) : null}
          {visibleBlocks.map((block, index) => {
            const open = openIds.has(block.id);
            const summary =
              (fields[block.labelKey] ?? '').trim() ||
              (fields[block.valueKey] ?? '')
                .replace(/<[^>]+>/g, '')
                .replace(/\n/g, ' ')
                .trim() ||
              'Vazio';
            return (
              <div
                key={block.id}
                className="overflow-hidden rounded-xl border border-border/70 bg-card shadow-glass-sm"
              >
                <div className="flex items-center gap-1 pr-1">
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-center gap-2 px-3 py-3 text-left transition-colors hover:bg-foreground/[0.03]"
                    onClick={() => toggleOpen(block.id)}
                    aria-expanded={open}
                  >
                    <ChevronDown
                      className={cn(
                        'size-4 shrink-0 text-muted-foreground transition-transform',
                        open && 'rotate-180',
                      )}
                    />
                    <CreditCard className="size-4 shrink-0 text-muted-foreground" />
                    <span className="shrink-0 text-sm font-semibold">{block.title}</span>
                    <span className="truncate text-xs text-muted-foreground">{summary}</span>
                  </button>
                  {index === visibleCount - 1 && visibleCount > 1 ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Remover bloco"
                      className="shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={removeLastBlock}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  ) : null}
                </div>
                {open ? (
                  <div className="space-y-3 border-t border-border/60 px-3 pb-3 pt-3">
                    <div className="flex flex-col gap-2">
                      <Label htmlFor={`${block.id}-label`}>Título</Label>
                      <Input
                        id={`${block.id}-label`}
                        value={fields[block.labelKey] ?? ''}
                        onChange={(e) => patch(block.labelKey, e.target.value)}
                        placeholder="Ex.: REGRA DE FIDELIDADE"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <Label htmlFor={`${block.id}-value`}>Conteúdo</Label>
                      <HtmlEditor
                        id={`${block.id}-value`}
                        value={walletValueToEditorHtml(fields[block.valueKey] ?? '')}
                        onChange={(html) => patch(block.valueKey, htmlToWalletValue(html))}
                        placeholder="Digite o texto… Enter quebra a linha como no Wallet"
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        <aside className="lg:sticky lg:top-24 lg:self-start">
          <VersoWalletPreview
            fields={fields}
            visibleCount={visibleCount}
            organizationName={passbook.organizationName}
            mgmUrl={showMgmBackLink ? mgmUrl : undefined}
          />
        </aside>
      </div>
    </>
  );
}
